import styled from "@emotion/styled";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNetworks } from "../api/hooks";
import { api } from "../lib/api";
import { theme } from "../lib/theme";
import { Button } from "./ui";

const Banner = styled.div`
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.danger};
  border-left-width: 4px;
  border-radius: ${theme.radius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Title = styled.div`
  font-size: ${theme.fontSize.md};
  font-weight: 600;
  color: ${theme.colors.danger};
`;

const NetworkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.sm} 0;
  border-top: 1px solid ${theme.colors.border};
  font-size: ${theme.fontSize.sm};
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

/**
 * Shown when the indexer has detected an upstream rollup-contract change
 * and is paused waiting for confirmation. Confirming wipes all DB data for
 * the affected network and applies the new protocol config — used for
 * nextnet's nightly redeploy. Intentionally not automatic: a misfire
 * would destroy real data on a mis-pointed indexer.
 */
export function PendingResetBanner() {
  const { data: networks } = useNetworks();
  const queryClient = useQueryClient();

  const pending = (networks ?? []).filter((n) => n.pendingReset);

  const confirm = useMutation({
    mutationFn: (id: string) => api.confirmNetworkReset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["networks"] });
    },
  });

  if (pending.length === 0) return null;

  return (
    <Banner>
      <Title>
        {pending.length === 1
          ? "Network reset pending confirmation"
          : `${pending.length} networks need reset confirmation`}
      </Title>
      <div style={{ fontSize: theme.fontSize.sm, color: theme.colors.text }}>
        The indexer detected a new rollup contract upstream. Confirming will wipe
        all indexed data for the network and start fresh from block 1 against the
        new chain. The indexer is paused until you confirm.
      </div>
      {pending.map((n) => {
        const pr = n.pendingReset!;
        return (
          <NetworkRow key={n.id}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ fontWeight: 600 }}>{n.name}</div>
              <Mono>
                {n.rollupAddress?.slice(0, 10)}… → {pr.rollupAddress.slice(0, 10)}…
                {" · detected "}{new Date(pr.detectedAt).toLocaleString()}
              </Mono>
            </div>
            <Button
              variant="danger"
              onClick={() => {
                if (
                  window.confirm(
                    `Reset ${n.name}? This deletes ALL indexed transactions, blocks, and analysis ` +
                      `output for this network. Cannot be undone.`,
                  )
                ) {
                  confirm.mutate(n.id);
                }
              }}
              disabled={confirm.isPending}
            >
              {confirm.isPending && confirm.variables === n.id ? "Resetting…" : "Confirm reset"}
            </Button>
          </NetworkRow>
        );
      })}
      {confirm.isError && (
        <div style={{ color: theme.colors.danger, fontSize: theme.fontSize.sm }}>
          Reset failed: {(confirm.error as Error).message}
        </div>
      )}
    </Banner>
  );
}
